import axios from 'axios'
import { describeLoadError, sendWebappLoadPerformance, WebappLoadSurface } from '@/lib/analytics/load-performance'

const axiosClient = axios.create()

axiosClient.defaults.baseURL = `/api`
axiosClient.defaults.headers.common['Content-Type'] = 'application/json'
axiosClient.defaults.headers.common['Accept'] = 'application/json'

type TrackedRequest = {
  startedAt: number
  surface: WebappLoadSurface
  main_category?: string
  category?: string
  sub_category?: string
}

const trackedRequests = new WeakMap<object, TrackedRequest>()

function getTrackedRequest(config: { url?: string; params?: unknown }): Omit<TrackedRequest, 'startedAt'> | null {
  const path = config.url?.split('?')[0]?.replace(/^\/api/, '')
  const surfaceByPath: Record<string, WebappLoadSurface> = {
    '/main-categories': 'main_categories',
    '/categories': 'categories',
    '/subcategories': 'subcategories',
    '/contents': 'contents',
  }
  const surface = path ? surfaceByPath[path] : undefined

  if (!surface) {
    return null
  }

  const params = config.params && typeof config.params === 'object' ? (config.params as Record<string, unknown>) : {}
  const getStringParam = (key: string) => (typeof params[key] === 'string' ? params[key] : undefined)

  return {
    surface,
    main_category: getStringParam('main_category'),
    category: getStringParam('category'),
    sub_category: getStringParam('sub_category'),
  }
}

axiosClient.interceptors.request.use((config) => {
  if (typeof window === 'undefined' || config.method?.toLowerCase() !== 'get') {
    return config
  }

  const trackedRequest = getTrackedRequest(config)

  if (trackedRequest) {
    trackedRequests.set(config, { ...trackedRequest, startedAt: performance.now() })
  }

  return config
})

axiosClient.interceptors.response.use(
  (response) => {
    const trackedRequest = trackedRequests.get(response.config)

    if (trackedRequest) {
      sendWebappLoadPerformance({
        surface: trackedRequest.surface,
        main_category: trackedRequest.main_category,
        category: trackedRequest.category,
        sub_category: trackedRequest.sub_category,
        operation: 'section_load',
        source: 'client',
        duration_ms: Math.round(performance.now() - trackedRequest.startedAt),
        status: 'success',
        service: 'next_api',
        ...nextServerTiming(response.headers['server-timing']),
        status_code: response.status,
        result_count: Array.isArray(response.data) ? response.data.length : undefined,
      })
      trackedRequests.delete(response.config)
    }

    return response
  },
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.config && !axios.isCancel(error)) {
      const trackedRequest = trackedRequests.get(error.config)

      if (trackedRequest) {
        sendWebappLoadPerformance({
          surface: trackedRequest.surface,
          main_category: trackedRequest.main_category,
          category: trackedRequest.category,
          sub_category: trackedRequest.sub_category,
          operation: 'section_load',
          source: 'client',
          duration_ms: Math.round(performance.now() - trackedRequest.startedAt),
          status: 'error',
          ...(error.response?.status ? { status_code: error.response.status } : {}),
          ...describeLoadError(error),
          service: 'next_api',
          ...nextServerTiming(error.response?.headers['server-timing']),
        })
        trackedRequests.delete(error.config)
      }
    }

    return Promise.reject(error)
  }
)

export default axiosClient

function nextServerTiming(value: unknown): { api_server_ms?: number } {
  if (typeof value !== 'string') return {}
  const match = /(?:^|,)\s*next_api;dur=([\d.]+)/.exec(value)
  const duration = match ? Number(match[1]) : NaN
  return Number.isFinite(duration) && duration >= 0 ? { api_server_ms: duration } : {}
}
